import React, { useState, useEffect } from 'react';
import { api } from './api.js';
import styles from './notificacion.module.css';
import { getUserData } from './permissions';

const Notifications = () => {
  const [notificaciones, setNotificaciones] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [sectores, setSectores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    const user = getUserData();
    setUserData(user);
    
    // Cargar usuarios, sectores y notificaciones
    Promise.all([
      api.getUsers(),
      api.getSectors()
    ]).then(([usersData, sectoresData]) => {
      setUsuarios(Array.isArray(usersData) ? usersData : []);
      setSectores(Array.isArray(sectoresData) ? sectoresData : []);
    }).catch(error => {
      console.error('Error al obtener usuarios/sectores:', error);
    });

    fetchNotificaciones();
    // Actualizar notificaciones cada 5 minutos
    const interval = setInterval(fetchNotificaciones, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotificaciones = async () => {
    try {
      // Primero actualizar/crear nuevas notificaciones de atrasos
      await api.checkAndCreateNotifications();
      
      // Luego obtener todas las notificaciones no leídas
      let data = await api.getNotifications();
      data = Array.isArray(data) ? data : [];
      
      // Si es encargado, filtrar solo atrasos de su sector
      if (userData?.rol === 'encargado_de_sector' && userData?.sector_id) {
        const sectorUser = sectores.find(s => String(s.id) === String(userData.sector_id));
        if (sectorUser) {
          data = data.filter(notif => notif.sector === sectorUser.nombre_sector);
        }
      }
      
      setNotificaciones(data);
    } catch (error) {
      console.error('Error al obtener notificaciones:', error);
      setNotificaciones([]);
    } finally {
      setLoading(false);
    }
  };

  const getEncargadoName = (sectorName) => {
    if (!sectorName || !usuarios.length) return 'N/A';
    
    // Encontrar el encargado asignado a este sector
    const encargado = usuarios.find(u => {
      if (u.rol !== 'encargado_de_sector') return false;
      
      // Buscar el nombre del sector por su ID
      const sector = sectores.find(s => String(s.id) === String(u.sector_id));
      return sector && sector.nombre_sector === sectorName;
    });
    
    return encargado ? `${encargado.nombre} ${encargado.apellido}` : 'No asignado';
  };

  const handleMarkAsRead = async (notificacionId) => {
    try {
      await api.markNotificationAsRead(notificacionId);
      // Remover de la lista
      setNotificaciones(notificaciones.filter(n => n.id !== notificacionId));
    } catch (error) {
      console.error('Error al marcar como leída:', error);
      alert('Error al marcar notificación como leída');
    }
  };

  const handleRestore = async (notificacion) => {
    try {
      // Crear un pago por el monto adeudado
      await api.createPayment({
        idAfiliado: notificacion.idAfiliado,
        monto: notificacion.monto_adeudado,
        fecha: new Date().toISOString().split('T')[0],
        metodo_pago: 'regularización'
      });

      // Marcar notificación como leída
      await api.markNotificationAsRead(notificacion.id);

      alert(`Pago de regularización registrado: $${notificacion.monto_adeudado.toFixed(2)}`);
      
      // Actualizar lista
      setNotificaciones(notificaciones.filter(n => n.id !== notificacion.id));
    } catch (error) {
      console.error('Error al registrar pago:', error);
      alert('Error al registrar pago de regularización');
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        gap: '20px',
        padding: '20px'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid rgba(0, 119, 221, 0.2)',
          borderTop: '4px solid #0077dd',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite'
        }}></div>
        <p style={{
          fontSize: '14px',
          color: '#0055aa',
          fontWeight: '600',
          letterSpacing: '0.5px',
          margin: '0'
        }}>Cargando notificaciones...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className={styles.notificationContainer}>
      <h2 className={styles.header}>Afiliados en Atraso</h2>
      <p className={styles.description}>
        Afiliados con más de 60 días sin registrar pagos.
      </p>
      <div className={styles.tableContainer}>
        <table className={styles.affiliatesTable}>
          <thead>
            <tr>
              <th>Código Afiliado</th>
              <th>Nombre y Apellido</th>
              <th>Sector</th>
              <th>Encargado de Sector</th>
              <th>Días de Atraso</th>
              <th>Monto Adeudado</th>
              <th>Último Pago</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {notificaciones.length > 0 ? (
              notificaciones.map((notif) => (
                <tr key={notif.id}>
                  <td>{notif.codigoAfiliado}</td>
                  <td>{notif.nombresTitular} {notif.apellidosTitular}</td>
                  <td>{notif.sector || 'N/A'}</td>
                  <td>{getEncargadoName(notif.sector)}</td>
                  <td>{notif.dias_atraso}</td>
                  <td>${parseFloat(notif.monto_adeudado).toFixed(2)}</td>
                  <td>{notif.ultimo_pago_fecha}</td>
                  <td>
                    <button
                      onClick={() => handleRestore(notif)}
                      className={styles.restoreButton}
                      title="Registrar pago de regularización"
                    >
                      Registrar Pago
                    </button>
                    <button
                      onClick={() => handleMarkAsRead(notif.id)}
                      className={styles.dismissButton}
                      title="Descartar notificación"
                    >
                      Descartar
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className={styles.noAfiliados}>
                  No hay afiliados en atraso.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Notifications;
